/* eslint-disable no-underscore-dangle */

'use-strict';

import { IScope } from 'angular';
import type { ObjectId } from 'mongodb';

import { MonitoringAPIService } from '../../shared/api/monitoring-api.service';
import { PrintAPIService } from '../../shared/api/print-api.service';
import { SalesAPIService } from '../../shared/api/sales-api.service';
import { InterfaceService } from '../../shared/interface/interface.service';
import { EnrichedTask, EventMonitoringEvent } from '../../src/app/@shared-types/event-monitoring';
import { ExpiringPricingSticker } from '../../src/app/@shared-types/print';
import { VehicleReservation } from '../../src/app/@shared-types/vehicle/availability';
import { ToastService, ToastType } from '../../ui-components/toast/toast.service';
import { PermissionsService } from '../authorization/permissions.service';
import { UserRole } from '../../src/app/@types/user';
import { TierService } from '../tenant/tier.service';
import { EFeatures } from '../../src/app/@shared-types/tenant/enums';
import { CustomerModel } from '../../src/app/shared/models/customer/customer';
import { EnrichedOpportunity } from '../../src/app/@shared-types/opportunites';
import { OpportunityModel } from '../../src/app/shared/models/opportunity/opportunity';
import { UsersService } from '../../shared/users/users.service';
import { EOpportunityState } from '../../src/app/@shared-types/opportunites/enums';
import TaskModel from '../../src/app/shared/models/task/task.model';
import TranslationService from '../../src/app/shared/models/translations/service';

interface OnlineError {
  vehicleId: ObjectId,
  model: string,
  errorSources: string,
  error: string,
}

interface FrontpageScope extends IScope {
  events: EventMonitoringEvent[] | null,
  eventsTotal: number,
  tasks: EnrichedTask[] | null,
  opportunities: EnrichedOpportunity[] | null,
  opportunitiesTotal: number,
  tasksTotal: number,
  onlineErrors: OnlineError[] | null,
  dates: {
    title: string,
    description: string,
    date: Date,
    link: string,
    color: string,
  }[] | null,
}

/* @ngInject */
export default function FrontpageController(
  $scope: FrontpageScope,
  toastService: ToastService,
  interfaceService: InterfaceService,
  monitoringApiService: MonitoringAPIService,
  salesApiService: SalesAPIService,
  printApiService: PrintAPIService,
  permissionsService: PermissionsService,
  tierService: TierService,
  customerModel: CustomerModel,
  opportunityModel: OpportunityModel,
  usersService: UsersService,
  taskModel: TaskModel,
  translationService: TranslationService,
): void {
  $scope.tasks = null;
  $scope.dates = null;
  $scope.onlineErrors = null;
  $scope.opportunities = null;
  $scope.opportunitiesTotal = 0;

  let reservations: VehicleReservation[] | null = null;
  let pricingStickers: ExpiringPricingSticker[] | null = null;

  const initOnlineErrors = async (): Promise<void> => {
    if (!tierService.authorizedToUseFeature(EFeatures.API_ONLINE_EXPORT)) {
      return;
    }
    if (!permissionsService.isPermitted([UserRole.ONLINE_ADMIN])) return;

    $scope.onlineErrors = [];

    const vehicles = await salesApiService.getVehiclesWithOnlineErrors();
    vehicles.forEach((vehicle) => {
      const errorSources: string[] = [];
      let errors: string[] = [];
      if (vehicle.apiData?.exportErrors?.length) {
        const translatedSources = vehicle.apiData.exportErrors
          .map((e) => translationService.translateExportAPI(e.apiIdentifier));

        errorSources.push(...translatedSources);

        errors = vehicle.apiData.exportErrors.map((e) => e.errors).flat();
      }

      const error = errors.length > 1 ? `${errors[0]} (${errors.length - 1} weitere Warnungen)` : errors[0];

      $scope.onlineErrors?.push({
        vehicleId: vehicle._id,
        model: `${vehicle.modelData?.make} ${vehicle.modelData?.model} ${vehicle.modelData?.modelDescription}`,
        errorSources: errorSources.join(', '),
        error,
      });
    });
  };

  const initTasks = async (): Promise<void> => {
    if (!tierService.authorizedToUseFeature(EFeatures.WORKFLOW_ADD)) return;

    const tasks = await taskModel.getAllOpen();

    /** Sort tasks by state, so that overdue tasks will be first to be shown. */
    $scope.tasks = tasks
      .filter((t) => t.state !== 'closed')
      .sort((a, b) => b.state.localeCompare(a.state));
    $scope.tasksTotal = tasks.length;
  };

  const getExpiringPricingStickers = (): Promise<void> => new Promise((resolve, reject) => {
    if (!tierService.authorizedToUseFeature(EFeatures.PRINT_PRICING)) {
      return resolve();
    }

    return printApiService.getExpiringPricingStickers()
      .then((res) => {
        pricingStickers = res;
        return resolve();
      })
      .catch((e) => reject(new Error(`Fehler beim Abrufen der ablaufenden Preisschilder: ${e}`)));
  });

  const getUpcomingReservations = (): Promise<void> => new Promise((resolve, reject) => {
    salesApiService.getUpcomingReservations()
      .then((res) => {
        reservations = res;
        return resolve();
      })
      .catch((e) => reject(new Error(`Fehler beim Abrufen der ablaufenden Reservierungen: ${e}`)));
  });

  const initDates = (): Promise<void> => new Promise((resolve, reject) => {
    const datesRequests = [];
    datesRequests.push(getUpcomingReservations());
    datesRequests.push(getExpiringPricingStickers());

    Promise.all(datesRequests)
      .then(async () => {
        $scope.dates = [];
        if (reservations) {
          for (const reservation of reservations) {
            // eslint-disable-next-line no-await-in-loop
            const customerName = await customerModel.getName(reservation.customerId);

            $scope.dates.push({
              title: `Reservierung von ${customerName} läuft aus`,
              description: '',
              date: reservation.endDate,
              link: `/vehicles/stock?id=${reservation.vehicleId}`,
              color: 'border-orange',
            });
          }
        }

        if (pricingStickers) {
          for (const pricingSticker of pricingStickers) {
            $scope.dates.push({
              title: 'Preisschild verliert Gültigkeit',
              description: 'Klicke, um zum Fahrzeug zu gelangen',
              date: pricingSticker.expiryDate,
              link: pricingSticker.link,
              color: 'border-blue',
            });
          }
        }

        $scope.dates = $scope.dates.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA.getTime() - dateB.getTime();
        });

        return resolve();
      })
      .catch((e) => {
        toastService.toast(ToastType.ERROR, e);
        return reject();
      });
  });

  const initOpportunities = async (): Promise<void> => {
    let ops = await opportunityModel.getAll({
      userId: [usersService.currentUserId()],
      state: [EOpportunityState.OPEN],
    });

    ops = ops.sort((a, b) => {
      const dateB = new Date(a.modificationDate);
      const dateA = new Date(b.modificationDate);
      return dateA.getTime() - dateB.getTime();
    });

    $scope.opportunitiesTotal = ops.length;
    $scope.opportunities = ops.splice(0, 10);
  };

  const init = () => {
    const frontpageRequests = [];
    // frontpageRequests.push(initEvents());
    frontpageRequests.push(initTasks());
    frontpageRequests.push(initOpportunities());
    frontpageRequests.push(initDates());
    frontpageRequests.push(initOnlineErrors());

    Promise.all(frontpageRequests)
      .then(() => {
        interfaceService.applyScope($scope);
      })
      .catch((e) => {
        toastService.toast(ToastType.ERROR, e);
      });
  };

  init();
}
